import DashboardTitleComponent from "../../custom/DashboardTitleComponent";
import React, { useEffect, useState, useRef } from "react";
import { Typography, Box, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";
import html2canvas from "html2canvas";
import { formatDateCard } from "../../../utils/date";
import CustomButton from "../../custom/CustomButton";
import Barcode from "react-barcode";
import newCard from "../../../images/IKDKI_New_Card.png";
import PhotoCropUploadModal from "../../custom/PhotoCropUploadModal";
import apiClient from "../../../utils/apiClient";
import { useUser } from "../../../pages/UserContext";

const UserProfileMenu = () => {
  const userDetails = useUser();
  const [namaLengkap, setNamaLengkap] = useState("");
  const [ktp, setKtp] = useState("");
  const [email, setEmail] = useState("");
  const [foto, setFoto] = useState("");
  const [role, setRole] = useState("");
  const [ikdkiId, setIkdkiId] = useState("");
  const [expiredCardDate, setExpiredCardDate] = useState("");
  const [prevExpiredCardDate, setPrevExpiredCardDate] = useState("");

  const [imageData, setImageData] = useState(null);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [isFetching, setIsFetching] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [photoLoadFailed, setPhotoLoadFailed] = useState(false);

  // upload photo
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  const [openModalUploadPhoto, setOpenModalUploadPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const vaNumber = `62367${ikdkiId ?? ""}`;


  const cardRef = useRef(null);

  const fetchCardData = async () => {
    try {
      const response = await apiClient.get("/users/details/all");

      const rawFoto = response.data.data.foto;

      // Cache-busted so a re-fetch after uploading a new photo (same
      // filename on the server) actually reloads the image instead of the
      // browser silently reusing the old cached bytes.
      const fotoPath =
        rawFoto && rawFoto !== "null"
          ? `${process.env.REACT_APP_BACKEND_HOST}/${rawFoto}?t=${Date.now()}`
          : `${process.env.REACT_APP_BACKEND_HOST}/images/photo_profile/ikdki.png`;

      setFoto(fotoPath);

      if (response.data.data.expired_card_at) {
        const formattedDate = new Date(response.data.data.expired_card_at);
        if (!isNaN(formattedDate)) {
          setExpiredCardDate(formattedDate);

          const prevDate = new Date(formattedDate);
          prevDate.setFullYear(prevDate.getFullYear() - 1);
          setPrevExpiredCardDate(formatDateCard(prevDate));
        }
      }

      // set status card name
      if (response.data.data.role === "admin") {
        response.data.data.role = "Dosen";
      }

      setExpiredCardDate(
        formatDateCard(response.data.data.expired_card_at)
      );
      setNamaLengkap(response.data.data.nama_lengkap);
      setKtp(response.data.data.ktp);
      setEmail(response.data.data.email);
      setRole(response.data.data.role);
      setIkdkiId(response.data.data.card_num);
    } catch (error) {
      console.error("Error fetching user details", error);
      setFetchFailed(true);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchCardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This page keeps its own separate copy of the profile data (fetched
  // above) rather than reading the shared context directly, so re-fetch once
  // KtpUpdateGate confirms an update — otherwise the card stays stuck
  // thinking the KTP is still unconfirmed until a manual page reload.
  useEffect(() => {
    if (userDetails?.is_ktp_updated) {
      fetchCardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails?.is_ktp_updated]);

  // After a new photo is uploaded, force the card to regenerate from the
  // fresh photo instead of reusing the previously generated card image.
  const handlePhotoUploaded = () => {
    setImageData(null);
    setImageLoaded(false);
    setPhotoLoadFailed(false);
    fetchCardData();
  };

  useEffect(() => {
    if (namaLengkap && imageLoaded) {
      setTimeout(() => {
        generateCardImage();
      }, 1000);
    }
  }, [namaLengkap, imageLoaded]);

  const generateCardImage = () => {
    if (cardRef.current) {
      setIsGenerating(true);
      html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        taintTest: false,
        logging: false,
        proxy: `${process.env.REACT_APP_BACKEND_HOST}/proxy`,
      })
        .then((canvas) => {
          const image = canvas.toDataURL("image/png");
          setImageData(image);
          setIsGenerating(false);
        })
        .catch((error) => {
          console.error("Error generating image:", error);
          setIsGenerating(false);
        });
    }
  };

  // upload photo profile
  const handlePhotoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImageSrc(URL.createObjectURL(file));
      setOpenModalUploadPhoto(true);
    }
    event.target.value = "";
  };

  const handleUploadProfilePhoto = () => {
    fileInputRef.current.click();
  };

  const handleDownloadCard = (namaLengkap, ktp) => {
    if (cardRef.current) {
      html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        proxy: process.env.REACT_APP_BACKEND_HOST,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/jpeg");
        setImageData(imgData);
        const link = document.createElement("a");
        link.href = imgData;
        link.download = `kartu_member_${namaLengkap}_${ktp}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    }
  };

  return (
    <div>
      <DashboardTitleComponent text={"Kartu Anggota"} />
      <Grid container justifyContent="flex-start">
        {" "}
        <Grid item>
          {isGenerating && (
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <Typography variant="body2">Loading Card...</Typography>
            </div>
          )}

          <div
            ref={cardRef}
            style={{
              display: isGenerating,
              position: "absolute",
              left: "-9999px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "flex-start",
              }}
            >
              <div
                style={{ position: "relative", width: "auto", height: "auto" }}
              >
                <img
                  src={newCard}
                  alt="New Card"
                  style={{ width: "auto", height: "100%", display: "block" }}
                />

                {foto && (
                  <img
                    src={foto}
                    alt="User"
                    onLoad={() => setImageLoaded(true)}
                    onError={(e) => {
                      console.error("Gagal memuat gambar:", e);
                      setImageLoaded(false);
                      setPhotoLoadFailed(true);
                    }}
                    style={{
                      position: "absolute",
                      top: "30%",
                      left: "10%",
                      width: "225px",
                      height: "300px",
                      objectFit: "cover",
                    }}
                  />
                )}

                <div
                  style={{
                    position: "absolute",
                    top: "30%",
                    left: "38%",
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    rowGap: "15px",
                    columnGap: "10px",
                    fontSize: "35px",
                    fontFamily: "Poppins, sans-serif",
                    color: "#00213A",
                  }}
                >
                  <span style={{ fontWeight: 400 }}>Nama :</span>
                  <span style={{ fontWeight: 600 }}>{namaLengkap}</span>

                  <span style={{ fontWeight: 400 }}>Status :</span>
                  <span style={{ fontWeight: 600 }}>{role}</span>

                  <span style={{ fontWeight: 400 }}>Nomor :</span>
                  <span style={{ fontWeight: 600 }}>{ikdkiId}</span>

                  {/* BARCODE di bawah tulisan Nomor */}
                  <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
                    {ikdkiId && (
                      <Barcode
                        value={String(ikdkiId)}
                        format="CODE128"
                        height={80}
                        width={2}
                        displayValue={false}
                        background="transparent"
                      />
                    )}
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    top: "80%",
                    left: "64%",
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    rowGap: "15px",
                    columnGap: "10px",
                    fontSize: "20px",
                    fontFamily: "Poppins, sans-serif",
                    color: "black",
                  }}
                >
                  <span style={{ fontWeight: 400 }}>Berlaku hingga {expiredCardDate}</span>
                </div>

                <div
                  style={{
                    position: "absolute",
                    top: "91%",
                    left: "32%",
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    rowGap: "15px",
                    columnGap: "10px",
                    fontSize: "30px",
                    fontFamily: "Poppins, sans-serif",
                    color: "#fff",
                  }}
                >
                  <span style={{ fontWeight: 400 }}>humasikdki@gmail.com</span>
                  <span style={{ fontWeight: 400 }}>| www.ikdki.org</span>
                </div>
              </div>
            </div>

          </div>
          {!isGenerating && imageData && (
            <Grid
              container
              spacing={3}
              alignItems="flex-start"
              sx={{ mt: 2 }}
            >
              {/* KARTU */}
              <Grid item xs={12} md={8}>
                <img
                  src={imageData}
                  alt="Generated Card"
                  style={{
                    width: "100%",
                    maxWidth: "500px",
                    height: "auto",
                    borderRadius: "12px",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                  }}
                />
              </Grid>

              {/* VA BCA */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    backgroundColor: "#f5f7fa",
                    border: "1px dashed #003158",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#003158", fontWeight: 500, mb: 1 }}
                  >
                    Nomor VA BCA
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: "#003158",
                      fontWeight: 700,
                      letterSpacing: "1px",
                      mb: 1,
                      wordBreak: "break-all",
                    }}
                  >
                    {vaNumber}
                  </Typography>

                  <Typography variant="caption" sx={{ color: "#555" }}>
                    Gunakan nomor VA ini untuk pembayaran keanggotaan
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {!imageData && (isFetching || (!fetchFailed && !photoLoadFailed)) && (
            // Fixed width here (not a %) because the ancestor <Grid item> a few
            // lines up has no xs/md size of its own, so it sizes to fit its
            // content instead of filling the row — a percentage-width child
            // would have nothing definite to resolve against and collapse to
            // ~0. The real <img> card doesn't hit this because browsers fall
            // back to a replaced element's intrinsic size in that situation;
            // Skeleton is a plain span, so it has no such fallback.
            <Box sx={{ width: { xs: 320, sm: 500, md: 780 }, mt: 2 }}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={280}
                  sx={{ maxWidth: 500, borderRadius: "12px" }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: "1px dashed #ddd",
                  }}
                >
                  <Skeleton width="60%" />
                  <Skeleton width="80%" height={32} sx={{ my: 1 }} />
                  <Skeleton width="90%" />
                </Box>
              </Grid>
            </Grid>
            </Box>
          )}

          {!imageData && !isFetching && (fetchFailed || photoLoadFailed) && (
            <Box
              sx={{
                mt: 3,
                p: 3,
                maxWidth: 420,
                borderRadius: 2,
                border: "1px dashed #A92027",
                backgroundColor: "#fdf1f1",
              }}
            >
              <Typography variant="subtitle1" sx={{ color: "#A92027", fontWeight: 600, mb: 1 }}>
                Kartu anggota gagal dimuat
              </Typography>
              <Typography variant="body2" sx={{ color: "#555", mb: namaLengkap ? 2 : 0 }}>
                {fetchFailed
                  ? "Tidak bisa mengambil data profil. Periksa koneksi lalu muat ulang halaman."
                  : "Foto profil belum tersedia atau gagal dimuat, sehingga kartu tidak bisa dibuat. Unggah foto profil terlebih dahulu."}
              </Typography>
              {namaLengkap && (
                <Typography variant="body2" sx={{ color: "#003158" }}>
                  {namaLengkap} &middot; {role} &middot; {ikdkiId}
                </Typography>
              )}
              {photoLoadFailed && !fetchFailed && (
                <Box sx={{ mt: 2 }}>
                  <CustomButton
                    text="Upload Foto Profil"
                    baseColor="#003158"
                    hoverColor="#00213A"
                    isFullWidth={false}
                    onClick={handleUploadProfilePhoto}
                  />
                </Box>
              )}
            </Box>
          )}

        </Grid>
        {!isGenerating && imageData && (
          <Grid container spacing={2} sx={{mt: 2}}>
            <Grid item md={4} xs={6}>
              <CustomButton
                text="Upload Foto Profil"
                baseColor="#003158"
                hoverColor="#00213A"
                isFullWidth={true}
                onClick={handleUploadProfilePhoto}
              />
            </Grid>
            <Grid item md={4} xs={6}>
              <CustomButton
                text="Download Kartu"
                baseColor="#A92027"
                hoverColor="#8C1016"
                isFullWidth={true}
                onClick={() => handleDownloadCard(namaLengkap, ktp)}
              />
            </Grid>
          </Grid>
        )}
      </Grid>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handlePhotoFileChange}
      />

      <PhotoCropUploadModal
        open={openModalUploadPhoto}
        onClose={() => setOpenModalUploadPhoto(false)}
        imageSrc={selectedImageSrc}
        email={email}
        onUploaded={handlePhotoUploaded}
      />
    </div>
  );
};

export default UserProfileMenu;
